import { useEffect } from "react";
import Box from "@mui/material/Box";
import { Outlet, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import AppSidebar from "../components/AppSidebar";
import Header from "../components/Header";
import ErrorBoundary from "../components/ErrorBoundary";
import Api from "../api/endPoints";
import { redirectToPlatformLogin, closeTabOrRedirectToLogin } from "../api/service";
import { setUser } from "../features/user/userSlice";
import { fetchAssetProfile } from "../features/assetProfile/assetProfileSlice";

const Layout = () => {
  const dispatch = useDispatch();
  const user = useSelector((state) => state.user.user);
  const location = useLocation();

  // The Vectr platform hands over only the token (captured from the URL at
  // boot); the user itself is fetched here once and kept in redux for the
  // header avatar / logout menu.
  useEffect(() => {
    if (user) return;
    if (!localStorage.getItem("token")) return;

    const fetchUserData = async () => {
      try {
        const response = await Api.getMe();
        if (response.status === 200 && response.data) {
          dispatch(setUser(response.data));
        } else if (response.status === 401) {
          localStorage.clear();
          redirectToPlatformLogin();
        } else {
          console.error("Fetching user failed:", response.status, response.data);
        }
      } catch (error) {
        console.error("Error fetching user data:", error);
      }
    };

    fetchUserData();
  }, [user, dispatch]);

  // The nsync profile id backing every device list/dashboard query. Fetched
  // here so it is in flight before any page mounts; the thunk's `condition`
  // makes the extra dispatches on later renders no-ops.
  useEffect(() => {
    if (!localStorage.getItem("token")) return;
    dispatch(fetchAssetProfile());
  }, [dispatch]);

  useEffect(() => {
    const handleStorageChange = (event) => {
      // Cross-tab: token removed/cleared in another tab — kill this tab too
      if (event.type === "storage" && !event.newValue) {
        localStorage.clear();
        closeTabOrRedirectToLogin();
      }

      // Same-tab: custom event dispatched when localStorage changes
      if (
        event.type === "vecmoconLocalStorageChanged" &&
        !localStorage.getItem("token")
      ) {
        localStorage.clear();
        closeTabOrRedirectToLogin();
      }
    };

    window.addEventListener("storage", handleStorageChange);
    window.addEventListener("vecmoconLocalStorageChanged", handleStorageChange);

    return () => {
      window.removeEventListener("storage", handleStorageChange);
      window.removeEventListener(
        "vecmoconLocalStorageChanged",
        handleStorageChange,
      );
    };
  }, []);

  return (
    <Box className="w-full h-screen relative bg-gray-800 flex">
      <AppSidebar />
      <div className="flex-1 h-full min-w-0 flex flex-col overflow-hidden bg-[#f2f4f7]">
        <Header />
        <div className="flex-1 overflow-y-auto px-5 py-4">
          <ErrorBoundary key={location.pathname}>
            <Outlet />
          </ErrorBoundary>
        </div>
      </div>
    </Box>
  );
};

export default Layout;
